import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import User from "../../../api/User";
import Auth from "../../../api/auth";
import Admin from "../../../api/Admin";
import { toast } from "react-hot-toast";
import { DashboardLayout } from "../../../components/organisms/Layout";
import useFormatPhoneNumber from "../../../hooks/FormatPhoneNumber";
import { selectUser, selectRoles } from "../../../store/authSlice";
import useQuery from "../../../hooks/useQuery";
import PaginationView from "../../../components/molecules/Pagination";
import SuperUser from "../../../api/SuperUser";
import Table from "../../../components/atoms/Tables/Table";
import { addIcon } from "../../../assets/images";
import IconSelector from "../../../components/atoms/Selector/IconSelector";
import SearchBar from "../../../components/atoms/SearchBar";
import debounce from "lodash/debounce";

const ManageBusinessUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatPhoneNumber = useFormatPhoneNumber;
  const query = useQuery();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectUser);
  const roles = useSelector(selectRoles);
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 10);
  const [metaData, setMetaData] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(query.get("search") || "");

  useEffect(() => {
    document.title = "HeyHire Portal-Users & Permissions";
  }, []);

  useEffect(() => {
    loadUsers();
  }, [query]);

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      newParams.page = 1;
      if (txt && txt.trim() !== "") {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }
      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const handleSearch = (value) => {
    setSearchQuery(value);
    debouncedUpdateSearch(value);
  };

  const loadUsers = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const sort = query.get("sort") || "";
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";

    const sortData = sort ? `sort=${sort}&` : "";
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = sortData + pageData + `&limit=${limit}` + searchData;

    // Update search input when loading from URL
    setSearchQuery(search);

    User.getAll(queryData)
      .then((response) => {
        const _users = response.data.data?.filter(
          (i) => i?.id !== currentUser?.id
        );
        setUsers(_users);
        setMetaData(response.data.meta);
      })
      .catch(() => {
        toast.error("Error trying to load users. Please try again.");
      })
      .then(() => setLoading(false));
  };

  const deleteUser = (userId) => {
    setLoading(true);
    User.deleteUser(userId)
      .then(() => {
        const dataCopy = users.slice();
        dataCopy.splice(
          users.findIndex((item) => item?.id === userId),
          1
        );
        setUsers(dataCopy);
        setLoading(false);
        toast.success("User Deleted.");
      })
      .catch(() => {
        setLoading(false);
        toast.error("Error trying to delete users. Please try again.");
      })
      .then(() => setLoading(false));
  };

  const requestResetPassword = (email) => {
    const body = {
      email: email,
    };
    Auth.ResetPasswordRequest(body)
      .then(() => {
        toast.success("Password reset email has been sent.");
      })
      .catch((error) => {
        toast.error("Something Went wrong. Please try again Later.");
      });
  };

  const resendWelcomeEmail = (userId) => {
    setLoading(true);
    SuperUser.resendWelcomeEmail(userId)
      .then(() => {
        toast.success("Email Sent.");
        setLoading(false);
      })
      .catch(() => {
        toast.error("Error trying to resend welcome email. Please try again.");
        setLoading(false);
      })
      .then(() => setLoading(false));
  };

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    const newParams = { ...Object.fromEntries(searchParams) };

    newParams.page = currentPage;
    newParams.pageSize = pageSize;

    if (searchQuery && searchQuery.trim() !== "") {
      newParams.search = searchQuery.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const handleLoginAsBusiness = (user) => {
    if (!user?.id) {
      toast.error("Unable to initiate impersonation. Missing user ID.");
      return;
    }

    // Call the admin impersonate API
    Admin.impersonate(user.id)
      .then((response) => {
        // Backend returns bearer token + user data (or redirect URL)
        const { token } = response.data;
        const businessAppUrl = process.env.REACT_APP_URL;

        // If backend returns a redirect URL, use it; otherwise construct URL with token
        let redirectUrl;
        if (token) {
          redirectUrl = `${businessAppUrl}/?impersonation_token=${token}`;
        } else {
          redirectUrl = businessAppUrl;
        }
        window.open(redirectUrl, '_blank');
      })
      .catch((error) => {
        // Handle errors
        if (error.response?.status === 401) {
          toast.error("Unauthorized. You don't have permission to impersonate this user.");
        } else if (error.response?.status === 403) {
          toast.error("Forbidden. You don't have permission to impersonate this user.");
        } else if (error.response?.status === 404) {
          toast.error("User not found.");
        } else {
          const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to impersonate user";
          toast.error(`Impersonation failed: ${errorMessage}`);
        }
        console.error("Impersonation error:", error);
      });
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader={t("business-users.business-users")}
          maxHeight="calc(100vh - 250px)"
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchQuery}
                  placeholder="Search by name, email, or company..."
                  searchFunction={handleSearch}
                />
              </div>
            </div>
          }
          headers={[
            t("common.full-name"),
            t("common.user-type"),
            t("common.email"),
            t("common.phone"),
            t("common.company"),
            t("common.last-login"),
            t("common.status"),
            t("common.action"),
          ]}
          emptyState={
            !loading &&
            users?.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No users found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : "Get started by adding a new user"}
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate("add")}
                    className="inline-flex items-center rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                  >
                    <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
                    Add User
                  </button>
                </div>
              </div>
            )
          }
          data={
            !loading &&
            users?.map((user, index) => (
              <tr
                key={index}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="font-semibold text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex flex-wrap gap-2">
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((role, roleIndex) => {
                        const roleName = role.name || role.role_name || role.role?.name || "Unknown Role";
                        return (
                          <span
                            key={roleIndex}
                            className="inline-flex items-center text-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                          >
                            {roleName.replace(/_/g, " ")}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-gray-400">No roles</span>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {user.email}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {formatPhoneNumber(user.phone) || "-"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {user.company.name}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm">
                  <div className="text-gray-700">
                    {user.last_login || "-"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                  <div className="flex items-center gap-2">
                    <button
                      className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                      onClick={() =>
                        navigate(
                          `/admin/manage-business-users/edit/${user?.id}`
                        )
                      }
                    >
                      Edit
                    </button>

                    {user.id !== currentUser.id && (
                      <>
                        <button
                          className="text-red-600 hover:text-red-900 font-medium hover:underline"
                          onClick={() => deleteUser(user?.id)}
                        >
                          Delete
                        </button>

                        <IconSelector
                          menus={[
                            ...(roles?.[0] === "super-user" ? [{
                              label: "Login as Business",
                              action: () => handleLoginAsBusiness(user),
                            }] : []),
                            {
                              label: "Send Reset Password Link",
                              action: () => requestResetPassword(user?.email),
                            },
                            {
                              label: "Resend Welcome Email",
                              action: () => resendWelcomeEmail(user?.id),
                            },
                            {
                              label: t("general-settings.edit-password"),
                              action: () =>
                                navigate(
                                  `/admin/manage-business-users/password-edit/${user?.id}`
                                ),
                            },
                          ]}
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          }
          buttonData={
            <button
              onClick={() => navigate("add")}
              className="w-full sm:w-auto flex-shrink-0 cursor-pointer flex justify-center items-center rounded-lg bg-primary-500 px-6 h-10 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 transition-all duration-200"
            >
              <img src={addIcon} alt="add" className="mr-2 w-4 h-4" />
              Add User
            </button>
          }
        />
        <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
          {!loading && metaData?.total > 0 && (
            <PaginationView
              current={metaData?.current_page}
              next={metaData?.to}
              pageSize={metaData?.per_page}
              total={metaData?.total}
              onChange={handlecPageChange}
            />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ManageBusinessUsers;
